import type { ClusterHint, SeverityLevel } from '@testflow/shared';
import type { DragEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { pickLang } from '../lib/pick-lang.js';

// #90: vier Stufen als Flächen-Badges, Tint-Muster (Nutzer-Feedback 2026-08-18): helle Flächen
// mit dunkler Schrift + Kontur im satten Ton (--sev-*-solid); Blocker ist die einzige Vollton-
// Badge. Kartenkante (edge) trägt immer den satten Ton. Tokens in styles/tokens/*.css;
// unbekannte color_token fallen neutral zurück (projekt-konfigurierbares Vokabular).
export function severityToneClass(colorToken: string | undefined): { edge: string; badge: string } {
  switch (colorToken) {
    case 'blocker':
      return {
        edge: 'border-l-[var(--sev-blocker-solid)]',
        badge:
          'border border-[var(--sev-blocker-solid)] bg-[var(--sev-blocker-bg)] text-[var(--sev-blocker-fg)]',
      };
    case 'high':
      return {
        edge: 'border-l-[var(--sev-high-solid)]',
        badge:
          'border border-[var(--sev-high-solid)] bg-[var(--sev-high-bg)] text-[var(--sev-high-fg)]',
      };
    case 'medium':
      return {
        edge: 'border-l-[var(--sev-medium-solid)]',
        badge:
          'border border-[var(--sev-medium-solid)] bg-[var(--sev-medium-bg)] text-[var(--sev-medium-fg)]',
      };
    case 'low':
      return {
        edge: 'border-l-[var(--sev-low-solid)]',
        badge:
          'border border-[var(--sev-low-solid)] bg-[var(--sev-low-bg)] text-[var(--sev-low-fg)]',
      };
    default:
      return { edge: 'border-l-[var(--border)]', badge: 'bg-bg-subtle text-text-muted' };
  }
}

export function SeverityBadge({ severity }: { severity: SeverityLevel | null }) {
  const { t, i18n } = useTranslation();
  if (!severity) {
    return (
      <span className="text-[10px] uppercase tracking-wide text-text-muted">
        {t('defect.severityNone')}
      </span>
    );
  }
  const tone = severityToneClass(severity.color_token);
  const label = pickLang(i18n.resolvedLanguage, severity.label_de, severity.label_en);
  return (
    <span
      className={`inline-flex items-center rounded-chip px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone.badge}`}
    >
      {label}
    </span>
  );
}

export function ClusterChip({ hints }: { hints: ClusterHint[] }) {
  const { t } = useTranslation();
  if (hints.length === 0) return null;
  const hasDup = hints.some((h) => h.kind === 'duplicate');
  return (
    <span
      title={hints.map((h) => `${h.human_id}: ${h.reason}`).join('\n')}
      className="rounded-chip border border-[var(--border)] px-1 py-0.5 text-[10px] text-status-warn"
    >
      ⚠ {hints.length} {hasDup ? t('defect.cluster.duplicate') : t('defect.cluster.related')}
    </span>
  );
}

export function AvatarBubble({ name }: { name: string | null }) {
  const initials = (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    // title = Vollname: auf der Karte steht nur das Kürzel, im MouseOver soll klar sein, wer gemeint ist.
    <span
      title={name ?? undefined}
      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-bg-subtle text-[10px] font-bold text-text-body"
    >
      {initials || '?'}
    </span>
  );
}

const DROP_TONE: Record<'idle' | 'allowed' | 'denied' | 'source', string> = {
  idle: '',
  allowed: 'ring-2 ring-brand-accent',
  denied: 'opacity-40',
  // Review-Fix Minor 6: ein Zustand ohne Wirkung wird entweder wirksam gemacht oder entfernt.
  // Die Ausgangsspalte bekommt eine dezente Markierung — deutlich zurueckhaltender als die
  // Hervorhebung der erlaubten Ziele (ring-2, volle Deckkraft): nur ein duenner Ring mit
  // reduzierter Deckkraft, ausschliesslich ueber vorhandene Tokens (kein Hex).
  source: 'ring-1 ring-inset ring-brand-accent/30',
};

export function KanbanColumn({
  title,
  // Review-Fix Minor 8: ohne Lane-Bezug tragen alle Lanes denselben Spaltennamen — ein
  // Screenreader kann die Lane nicht mithoeren, mehrere Gruppen heissen identisch. Optional,
  // damit die Atom-Tests dieser Datei (eine einzelne Spalte ohne Lane-Kontext) unveraendert
  // bleiben; das Board (mehrere Lanes) reicht ihn immer mit.
  laneName,
  count,
  dropState,
  onDragOver,
  onDrop,
  children,
}: {
  title: string;
  laneName?: string;
  count: number;
  dropState: 'idle' | 'allowed' | 'denied' | 'source';
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
  children: ReactNode;
}) {
  const ariaLabel = laneName ? `${laneName} · ${title}` : title;
  return (
    // role=group + aria-label: die Spalte ist ein benanntes Ziel — sonst haette der Test keinen
    // stabilen Griff und ein Screenreader keine Ansage. data-drop ist der Testhaken fuer den
    // Zustand, den die Klasse nur farblich ausdrueckt.
    // biome-ignore lint/a11y/useSemanticElements: kein Formular — <fieldset> brächte eine eigene UA-Stildatei und legend-getriebene Namensberechnung mit, die hier nicht passt.
    <div
      role="group"
      aria-label={ariaLabel}
      data-drop={dropState}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`flex min-w-[240px] flex-1 flex-col gap-2 rounded-card bg-bg-subtle p-2 ${DROP_TONE[dropState]}`}
    >
      <div className="flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        <span>{title}</span>
        <span>{count}</span>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}
